/**
 * /app/hardware — Hardware Integration Platform (HIP).
 * Admin UI: alta/gestión de dispositivos, catálogo de drivers, estado, pruebas,
 * diagnóstico, logs, permisos por plan y simulación (sin hardware real).
 * Gateado por el product flag "hardware" (nav hard-gate en app-nav.ts).
 */
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Plus, RefreshCw, Trash2, Power, Wrench, Stethoscope, ScrollText, ShieldCheck, FlaskConical, Cable } from "lucide-react";
import { deviceTypesForBusinessProfile, requiredFlagForConnectionType } from "@/lib/hardware/capabilities";
import { formatPlanTierLabel } from "@structura/core/capability-gate";
import type { HardwareBindingRole, HardwareBindings } from "@/lib/hardware/bindings";
import { CrudEmptyState, CrudShellHeader, Loading, Panel, DataTable } from "@/components/ds";
import type { DataTableColumn } from "@/components/ds";
import { ClinicalPageShell } from "@/components/clinical";
import { HardwareTour } from "@/components/ProductTour";
import { FeatureLockedState } from "@/components/workspace/FeatureLockedState";
import type { PlanApiErrorPayload } from "@structura/core/plan-api-errors";
import { useWorkspaceTenantId } from "@/hooks/use-workspace-tenant-id";
import { useTenantProductFlags } from "@/hooks/use-tenant-product-flags";
import { apiGetJson, apiSendJson, apiDelete } from "@/lib/sm-api";
import { INVOICING_PATHS, InvoicingDisclaimer } from "@/components/invoicing/InvoicingPathsHelp";
import { cn } from "@/lib/utils";
import {
  HARDWARE_DEVICE_TYPES,
  HARDWARE_DEVICE_TYPE_LABELS,
  HARDWARE_CONNECTION_TYPE_LABELS,
  type HardwareDeviceType,
  type HardwareConnectionType,
  type HardwareDeviceRecord,
  type HardwareEventRecord,
  type HardwareLogRecord,
} from "@/lib/hardware/types";
import {
  TENANT_PRODUCT_FLAG_KEYS,
  TENANT_PRODUCT_FLAG_META,
  TENANT_PRODUCT_FLAG_PLAN_TIER,
  type TenantProductFlagKey,
} from "@/lib/tenant-product-flags";
import { useAppDialogs } from "@/components/ui/app-dialogs";
import { MerchantExperienceBar } from "@/components/renata/MerchantExperienceBar";

const HW_FLAG_KEYS = TENANT_PRODUCT_FLAG_KEYS.filter((k) => k.startsWith("hardware")) as TenantProductFlagKey[];

type HardwareTab = "devices" | "bindings" | "drivers" | "status" | "test" | "diagnostics" | "logs" | "permissions" | "simulation";
const TABS: { id: HardwareTab; label: string; icon: typeof Plus }[] = [
  { id: "devices", label: "Dispositivos", icon: Plus },
  { id: "bindings", label: "Asignaciones", icon: Cable },
  { id: "drivers", label: "Drivers", icon: Wrench },
  { id: "status", label: "Estado", icon: Power },
  { id: "test", label: "Pruebas", icon: FlaskConical },
  { id: "diagnostics", label: "Diagnóstico", icon: Stethoscope },
  { id: "logs", label: "Logs", icon: ScrollText },
  { id: "permissions", label: "Permisos", icon: ShieldCheck },
  { id: "simulation", label: "Simulación", icon: RefreshCw },
];

interface DriverDescriptor {
  driverId: string;
  deviceType: HardwareDeviceType;
  capabilities: readonly string[];
  simulated: boolean;
}

export const Route = createFileRoute("/app/hardware")({
  component: HardwarePage,
  validateSearch: (s: Record<string, unknown>) => ({
    tab: (TABS.some((t) => t.id === s.tab) ? (s.tab as HardwareTab) : "devices") as HardwareTab,
  }),
  head: () => ({ meta: [{ title: "Hardware — Structura Market" }] }),
});

function HardwarePage() {
  const tenantId = useWorkspaceTenantId();
  const navigate = useNavigate({ from: "/app/hardware" });
  const search = Route.useSearch();
  const active = search.tab;

  const [devices, setDevices] = useState<HardwareDeviceRecord[]>([]);
  const { confirm } = useAppDialogs();
  const [drivers, setDrivers] = useState<DriverDescriptor[]>([]);
  const [logs, setLogs] = useState<HardwareLogRecord[]>([]);
  const [events, setEvents] = useState<HardwareEventRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [lock, setLock] = useState<PlanApiErrorPayload | null>(null);
  const productFlags = useTenantProductFlags();

  const loadCore = useCallback(async () => {
    if (!tenantId) return;
    setLock(null);
    try {
      const [d, dr] = await Promise.all([
        apiGetJson<{ devices: HardwareDeviceRecord[] }>("/api/hardware/devices", tenantId),
        apiGetJson<{ drivers: DriverDescriptor[] }>("/api/hardware/drivers", tenantId),
      ]);
      setDevices(d.devices ?? []);
      setDrivers(dr.drivers ?? []);
    } catch (e: unknown) {
      const err = e as Error & { planLock?: PlanApiErrorPayload };
      if (err.planLock) setLock(err.planLock);
      else toast.error("Error al cargar Hardware");
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  const loadLogs = useCallback(async () => {
    if (!tenantId) return;
    try {
      const r = await apiGetJson<{ logs: HardwareLogRecord[] }>("/api/hardware/logs", tenantId);
      setLogs(r.logs ?? []);
    } catch {
      toast.error("Error al cargar logs");
    }
  }, [tenantId]);

  const loadEvents = useCallback(async () => {
    if (!tenantId) return;
    try {
      const r = await apiGetJson<{ events: HardwareEventRecord[] }>("/api/hardware/events", tenantId);
      setEvents(r.events ?? []);
    } catch {
      toast.error("Error al cargar eventos");
    }
  }, [tenantId]);

  useEffect(() => { loadCore(); }, [loadCore]);
  useEffect(() => {
    if (active === "logs") void loadLogs();
    if (active === "simulation") void loadEvents();
  }, [active, loadLogs, loadEvents]);

  function setTab(tab: HardwareTab) {
    void navigate({ search: { tab } });
  }

  async function runAction(deviceId: string, action: string, payload?: Record<string, unknown>) {
    try {
      const r = await apiSendJson<{ result: unknown }>(`/api/hardware/devices/${deviceId}/action`, "POST", { action, payload }, tenantId);
      toast.success(`${action}: ${JSON.stringify(r.result)}`);
      await loadCore();
      return r.result;
    } catch (err) {
      toast.error(err instanceof Error ? err.message : `Falló la acción '${action}'`);
      return null;
    }
  }

  async function toggleEnabled(device: HardwareDeviceRecord) {
    try {
      await apiSendJson(`/api/hardware/devices/${device.id}`, "PATCH", { enabled: !device.enabled }, tenantId);
      await loadCore();
    } catch {
      toast.error("Error al actualizar dispositivo");
    }
  }

  async function deleteDevice(device: HardwareDeviceRecord) {
    if (!(await confirm({ title: `¿Eliminar "${device.name}"?`, destructive: true, confirmLabel: "Eliminar" }))) return;
    try {
      await apiDelete(`/api/hardware/devices/${device.id}`, tenantId);
      toast.success("Dispositivo eliminado");
      await loadCore();
    } catch {
      toast.error("Error al eliminar");
    }
  }

  if (lock) return <FeatureLockedState payload={lock} />;

  return (
    <div>
      <HardwareTour />
      <CrudShellHeader title="Hardware" description="Dispositivos POS conectados — drivers, estado, pruebas y diagnóstico" />
      <ClinicalPageShell offset="none">
        <MerchantExperienceBar screenId="hardware" />
        <nav aria-label="Secciones de hardware" className="mb-4 flex gap-1 overflow-x-auto border-b border-border/40">
          {TABS.map((t) => (
            <button
              key={t.id}
              type="button"
              onClick={() => setTab(t.id)}
              className={cn(
                "flex flex-shrink-0 items-center gap-1.5 border-b-2 px-3 py-2 text-sm transition-colors",
                active === t.id ? "border-primary text-foreground" : "border-transparent text-muted-foreground hover:text-foreground",
              )}
              aria-current={active === t.id ? "page" : undefined}
            >
              <t.icon className="h-3.5 w-3.5" />
              {t.label}
            </button>
          ))}
        </nav>

        {loading ? (
          <Loading variant="list" rows={4} />
        ) : (
          <>
            {active === "devices" && (
              <DevicesTab devices={devices} drivers={drivers} tenantId={tenantId} productFlags={productFlags} onReload={loadCore} onToggle={toggleEnabled} onDelete={deleteDevice} />
            )}
            {active === "bindings" && (
              <BindingsTab devices={devices} tenantId={tenantId} />
            )}
            {active === "drivers" && <DriversTab drivers={drivers} />}
            {active === "status" && <StatusTab devices={devices} onCheck={(d) => runAction(d.id, "health")} />}
            {active === "test" && <TestTab devices={devices} drivers={drivers} onRun={runAction} />}
            {active === "diagnostics" && <DiagnosticsTab devices={devices} onRun={runAction} />}
            {active === "logs" && <LogsTab logs={logs} onReload={loadLogs} />}
            {active === "permissions" && <PermissionsTab flags={productFlags} />}
            {active === "simulation" && (
              <SimulationTab tenantId={tenantId} drivers={drivers} devices={devices} events={events} onReload={async () => { await loadCore(); await loadEvents(); }} />
            )}
          </>
        )}
      </ClinicalPageShell>
    </div>
  );
}

// ─── Dispositivos ───────────────────────────────────────────────────────────

function DevicesTab({
  devices, drivers, tenantId, productFlags, onReload, onToggle, onDelete,
}: {
  devices: HardwareDeviceRecord[]; drivers: DriverDescriptor[]; tenantId: string;
  productFlags: Record<string, boolean> | undefined;
  onReload: () => Promise<void>; onToggle: (d: HardwareDeviceRecord) => Promise<void>; onDelete: (d: HardwareDeviceRecord) => Promise<void>;
}) {
  const [showForm, setShowForm] = useState(false);
  const [deviceType, setDeviceType] = useState<HardwareDeviceType>("pos_terminal");
  const [driverId, setDriverId] = useState("");
  const [name, setName] = useState("");
  const [connectionType, setConnectionType] = useState<HardwareConnectionType>("simulated");
  const [saving, setSaving] = useState(false);
  const [profileTypes, setProfileTypes] = useState<readonly HardwareDeviceType[]>(HARDWARE_DEVICE_TYPES);

  // SCRUM-243: "simulated" siempre disponible; el resto depende de su propio
  // flag de plan (mismo gate que valida el servidor en POST/PATCH).
  const connectionAvailability = useMemo(() => {
    const map = {} as Record<HardwareConnectionType, boolean>;
    for (const type of Object.keys(HARDWARE_CONNECTION_TYPE_LABELS) as HardwareConnectionType[]) {
      const flag = requiredFlagForConnectionType(type);
      map[type] = flag ? productFlags?.[flag] === true : true;
    }
    return map;
  }, [productFlags]);

  useEffect(() => {
    if (!connectionAvailability[connectionType]) setConnectionType("simulated");
  }, [connectionAvailability, connectionType]);

  useEffect(() => {
    if (!tenantId) return;
    apiGetJson<{ businessProfile?: string }>("/api/tenant", tenantId)
      .then((r) => {
        setProfileTypes(deviceTypesForBusinessProfile(r.businessProfile ?? null));
      })
      .catch(() => setProfileTypes(HARDWARE_DEVICE_TYPES));
  }, [tenantId]);

  const driversForType = useMemo(() => drivers.filter((d) => d.deviceType === deviceType), [drivers, deviceType]);

  useEffect(() => {
    if (!driversForType.some((d) => d.driverId === driverId)) {
      setDriverId(driversForType[0]?.driverId ?? "");
    }
  }, [driversForType, driverId]);

  async function handleCreate() {
    if (!name.trim() || !driverId) return;
    setSaving(true);
    try {
      await apiSendJson("/api/hardware/devices", "POST", { name, deviceType, driverId, connectionType }, tenantId);
      toast.success("Dispositivo creado");
      setShowForm(false);
      setName("");
      await onReload();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Error al crear dispositivo");
    } finally {
      setSaving(false);
    }
  }

  const columns: DataTableColumn<HardwareDeviceRecord>[] = [
    { key: "name", header: "Nombre", render: (r) => <span className="font-medium">{r.name}</span> },
    { key: "type", header: "Tipo", render: (r) => HARDWARE_DEVICE_TYPE_LABELS[r.deviceType] },
    { key: "driver", header: "Driver", render: (r) => <code className="text-xs">{r.driverId}</code> },
    { key: "conn", header: "Conexión", render: (r) => HARDWARE_CONNECTION_TYPE_LABELS[r.connectionType] },
    { key: "status", header: "Estado", render: (r) => <StatusBadge status={r.status} /> },
    { key: "enabled", header: "Activo", render: (r) => (r.enabled ? "Sí" : "No") },
    {
      key: "actions", header: "", render: (r) => (
        <div className="flex gap-2">
          <button type="button" onClick={() => onToggle(r)} className="text-xs text-muted-foreground hover:text-foreground">
            {r.enabled ? "Deshabilitar" : "Habilitar"}
          </button>
          <button type="button" onClick={() => onDelete(r)} className="text-destructive hover:opacity-80">
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <button type="button" onClick={() => setShowForm((v) => !v)} className="flex items-center gap-1.5 rounded-lg bg-primary px-3 py-1.5 text-sm text-primary-foreground">
          <Plus className="h-3.5 w-3.5" /> Agregar dispositivo
        </button>
      </div>

      {showForm && (
        <Panel>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <label className="text-xs text-muted-foreground">
              Nombre
              <input value={name} onChange={(e) => setName(e.target.value)} className="mt-1 w-full rounded-md border border-border bg-background px-2 py-1.5 text-sm" placeholder="Balanza mostrador" />
            </label>
            <label className="text-xs text-muted-foreground">
              Tipo de dispositivo
              <select value={deviceType} onChange={(e) => setDeviceType(e.target.value as HardwareDeviceType)} className="mt-1 w-full rounded-md border border-border bg-background px-2 py-1.5 text-sm">
                {profileTypes.map((t) => <option key={t} value={t}>{HARDWARE_DEVICE_TYPE_LABELS[t]}</option>)}
              </select>
              <span className="mt-1 block text-[10px] text-muted-foreground">Filtrado por rubro del negocio. Podés dar de alta otros tipos desde Simulación.</span>
            </label>
            <label className="text-xs text-muted-foreground">
              Driver
              <select value={driverId} onChange={(e) => setDriverId(e.target.value)} className="mt-1 w-full rounded-md border border-border bg-background px-2 py-1.5 text-sm">
                {driversForType.map((d) => <option key={d.driverId} value={d.driverId}>{d.driverId}</option>)}
              </select>
            </label>
            <label className="text-xs text-muted-foreground">
              Conexión
              <select value={connectionType} onChange={(e) => setConnectionType(e.target.value as HardwareConnectionType)} className="mt-1 w-full rounded-md border border-border bg-background px-2 py-1.5 text-sm">
                {(Object.entries(HARDWARE_CONNECTION_TYPE_LABELS) as [HardwareConnectionType, string][]).map(([k, label]) => {
                  const available = connectionAvailability[k];
                  const flag = requiredFlagForConnectionType(k);
                  const planHint = !available && flag ? ` (requiere plan ${formatPlanTierLabel(TENANT_PRODUCT_FLAG_PLAN_TIER[flag])})` : "";
                  return (
                    <option key={k} value={k} disabled={!available}>
                      {label}{planHint}
                    </option>
                  );
                })}
              </select>
            </label>
          </div>
          <div className="mt-3 flex justify-end gap-2">
            <button type="button" onClick={() => setShowForm(false)} className="rounded-md px-3 py-1.5 text-sm text-muted-foreground">Cancelar</button>
            <button type="button" disabled={saving || !name.trim() || !driverId} onClick={handleCreate} className="rounded-md bg-primary px-3 py-1.5 text-sm text-primary-foreground disabled:opacity-50">
              {saving ? "Guardando…" : "Crear"}
            </button>
          </div>
        </Panel>
      )}

      {devices.length === 0 ? (
        <CrudEmptyState title="Sin dispositivos" description="Agregá tu primer dispositivo simulado para empezar a probar la plataforma de hardware." />
      ) : (
        <DataTable columns={columns} rows={devices} getRowKey={(r) => r.id} />
      )}
    </div>
  );
}

function StatusBadge({ status }: { status: string }) {
  const colors: Record<string, string> = {
    connected: "bg-emerald-500/20 text-emerald-300",
    disconnected: "bg-zinc-500/20 text-zinc-300",
    error: "bg-red-500/20 text-red-300",
    busy: "bg-amber-500/20 text-amber-300",
    unknown: "bg-zinc-500/20 text-zinc-400",
  };
  return <span className={cn("rounded px-2 py-0.5 text-xs", colors[status] ?? colors.unknown)}>{status}</span>;
}

// ─── Drivers ────────────────────────────────────────────────────────────────

function DriversTab({ drivers }: { drivers: DriverDescriptor[] }) {
  const columns: DataTableColumn<DriverDescriptor>[] = [
    { key: "driverId", header: "Driver", render: (r) => <code className="text-xs">{r.driverId}</code> },
    { key: "type", header: "Tipo de dispositivo", render: (r) => HARDWARE_DEVICE_TYPE_LABELS[r.deviceType] },
    { key: "caps", header: "Capacidades", render: (r) => r.capabilities.join(", ") },
    { key: "sim", header: "Origen", render: (r) => (r.simulated ? "Simulado" : "Real") },
  ];
  return drivers.length === 0
    ? <CrudEmptyState title="Sin drivers" description="No hay drivers registrados." />
    : <DataTable columns={columns} rows={drivers} getRowKey={(r) => r.driverId} />;
}

// ─── Estado ─────────────────────────────────────────────────────────────────

function StatusTab({ devices, onCheck }: { devices: HardwareDeviceRecord[]; onCheck: (d: HardwareDeviceRecord) => Promise<unknown> }) {
  const columns: DataTableColumn<HardwareDeviceRecord>[] = [
    { key: "name", header: "Nombre", render: (r) => r.name },
    { key: "status", header: "Estado", render: (r) => <StatusBadge status={r.status} /> },
    { key: "seen", header: "Últ. contacto", render: (r) => (r.lastSeenAt ? new Date(r.lastSeenAt).toLocaleString("es-AR") : "—") },
    { key: "check", header: "", render: (r) => (
      <button type="button" onClick={() => onCheck(r)} className="flex items-center gap-1 text-xs text-primary hover:opacity-80">
        <RefreshCw className="h-3 w-3" /> Verificar
      </button>
    ) },
  ];
  return devices.length === 0
    ? <CrudEmptyState title="Sin dispositivos" description="Agregá dispositivos en la pestaña Dispositivos." />
    : <DataTable columns={columns} rows={devices} getRowKey={(r) => r.id} />;
}

// ─── Pruebas ────────────────────────────────────────────────────────────────

function TestTab({
  devices, drivers, onRun,
}: { devices: HardwareDeviceRecord[]; drivers: DriverDescriptor[]; onRun: (id: string, action: string, payload?: Record<string, unknown>) => Promise<unknown> }) {
  const [selectedId, setSelectedId] = useState(devices[0]?.id ?? "");
  const device = devices.find((d) => d.id === selectedId);
  const driver = drivers.find((d) => d.driverId === device?.driverId);
  const [result, setResult] = useState<unknown>(null);

  if (devices.length === 0) return <CrudEmptyState title="Sin dispositivos" description="Agregá dispositivos para poder probarlos." />;

  const capabilityActions: Record<string, { action: string; label: string; payload?: Record<string, unknown> }> = {
    print: { action: "print", label: "Imprimir prueba", payload: { content: "PRUEBA HIP", kind: "receipt" } },
    weigh: { action: "weigh", label: "Pesar" },
    scan: { action: "scan", label: "Escanear" },
    pay: { action: "pay", label: "Cobrar $100 (test)", payload: { amount: 100, currency: "ARS", reference: "test" } },
    open_drawer: { action: "openDrawer", label: "Abrir cajón" },
    display_message: { action: "displayMessage", label: "Mostrar mensaje", payload: { lines: ["Hola", "HIP"] } },
  };

  return (
    <div className="space-y-4">
      <label className="block text-xs text-muted-foreground">
        Dispositivo
        <select value={selectedId} onChange={(e) => { setSelectedId(e.target.value); setResult(null); }} className="mt-1 w-full max-w-sm rounded-md border border-border bg-background px-2 py-1.5 text-sm">
          {devices.map((d) => <option key={d.id} value={d.id}>{d.name} — {HARDWARE_DEVICE_TYPE_LABELS[d.deviceType]}</option>)}
        </select>
      </label>

      {driver && (
        <div className="flex flex-wrap gap-2">
          {driver.capabilities.map((cap) => {
            const spec = capabilityActions[cap];
            if (!spec) return null;
            return (
              <button
                key={cap}
                type="button"
                onClick={async () => setResult(await onRun(device!.id, spec.action, spec.payload))}
                className="rounded-lg border border-border px-3 py-1.5 text-sm hover:bg-secondary/40"
              >
                {spec.label}
              </button>
            );
          })}
        </div>
      )}

      {result !== null && (
        <Panel>
          <pre className="whitespace-pre-wrap text-xs">{JSON.stringify(result, null, 2)}</pre>
        </Panel>
      )}
    </div>
  );
}

// ─── Diagnóstico ────────────────────────────────────────────────────────────

function DiagnosticsTab({ devices, onRun }: { devices: HardwareDeviceRecord[]; onRun: (id: string, action: string) => Promise<unknown> }) {
  const [results, setResults] = useState<Record<string, unknown>>({});

  if (devices.length === 0) return <CrudEmptyState title="Sin dispositivos" description="Agregá dispositivos para diagnosticar." />;

  return (
    <div className="space-y-3">
      {devices.map((d) => (
        <Panel key={d.id}>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium">{d.name}</p>
              <p className="text-xs text-muted-foreground">{HARDWARE_DEVICE_TYPE_LABELS[d.deviceType]} — {d.driverId}</p>
            </div>
            <button
              type="button"
              onClick={async () => setResults((prev) => ({ ...prev, [d.id]: undefined }) )}
              className="hidden"
            />
            <button
              type="button"
              onClick={async () => {
                const r = await onRun(d.id, "diagnostics");
                setResults((prev) => ({ ...prev, [d.id]: r }));
              }}
              className="flex items-center gap-1 rounded-lg border border-border px-3 py-1.5 text-sm hover:bg-secondary/40"
            >
              <Stethoscope className="h-3.5 w-3.5" /> Ejecutar diagnóstico
            </button>
          </div>
          {results[d.id] !== undefined && (
            <pre className="mt-3 whitespace-pre-wrap text-xs">{JSON.stringify(results[d.id], null, 2)}</pre>
          )}
        </Panel>
      ))}
    </div>
  );
}

// ─── Logs ───────────────────────────────────────────────────────────────────

function LogsTab({ logs, onReload }: { logs: HardwareLogRecord[]; onReload: () => Promise<void> }) {
  const levelColors: Record<string, string> = {
    error: "text-red-300", warn: "text-amber-300", info: "text-foreground", debug: "text-muted-foreground",
  };
  const columns: DataTableColumn<HardwareLogRecord>[] = [
    { key: "when", header: "Fecha", render: (r) => new Date(r.createdAt).toLocaleString("es-AR") },
    { key: "level", header: "Nivel", render: (r) => <span className={levelColors[r.level] ?? ""}>{r.level}</span> },
    { key: "message", header: "Mensaje", render: (r) => r.message },
    { key: "device", header: "Dispositivo", render: (r) => r.deviceId ?? "—" },
  ];
  return (
    <div className="space-y-3">
      <div className="flex justify-end">
        <button type="button" onClick={() => onReload()} className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground">
          <RefreshCw className="h-3 w-3" /> Actualizar
        </button>
      </div>
      {logs.length === 0
        ? <CrudEmptyState title="Sin logs" description="Todavía no hay eventos de diagnóstico registrados." />
        : <DataTable columns={columns} rows={logs} getRowKey={(r) => r.id} />}
    </div>
  );
}

// ─── Permisos ───────────────────────────────────────────────────────────────

function PermissionsTab({ flags }: { flags: Record<string, boolean> | undefined }) {
  const columns: DataTableColumn<TenantProductFlagKey>[] = [
    { key: "flag", header: "Capability", render: (k) => <code className="text-xs">{k}</code> },
    { key: "label", header: "Descripción", render: (k) => TENANT_PRODUCT_FLAG_META[k] },
    { key: "tier", header: "Plan mínimo", render: (k) => <span className="capitalize">{TENANT_PRODUCT_FLAG_PLAN_TIER[k]}</span> },
    { key: "on", header: "Habilitado", render: (k) => (flags?.[k] ? <span className="text-emerald-300">Sí</span> : <span className="text-muted-foreground">No</span>) },
  ];
  return (
    <div className="space-y-3">
      <p className="text-xs text-muted-foreground">
        Cada tipo de dispositivo y capacidad avanzada de HIP tiene su propio feature flag, validado en cada request
        (<code>assertSmTenantProductFlag</code>). Este panel es solo lectura — el plan del tenant se administra
        desde el panel de plataforma (Admin).
      </p>
      <DataTable columns={columns} rows={HW_FLAG_KEYS} getRowKey={(k) => k} />
    </div>
  );
}

// ─── Simulación ─────────────────────────────────────────────────────────────

function SimulationTab({
  tenantId, drivers, devices, events, onReload,
}: { tenantId: string; drivers: DriverDescriptor[]; devices: HardwareDeviceRecord[]; events: HardwareEventRecord[]; onReload: () => Promise<void> }) {
  const [creating, setCreating] = useState<string | null>(null);

  async function quickCreate(deviceType: HardwareDeviceType) {
    const driver = drivers.find((d) => d.deviceType === deviceType);
    if (!driver) return;
    setCreating(deviceType);
    try {
      await apiSendJson("/api/hardware/devices", "POST", {
        name: `${HARDWARE_DEVICE_TYPE_LABELS[deviceType]} (demo)`,
        deviceType,
        driverId: driver.driverId,
        connectionType: "simulated",
      }, tenantId);
      toast.success(
        deviceType === "fiscal_controller"
          ? "Controlador fiscal simulado creado y asignado (si el rol estaba vacío)"
          : "Dispositivo simulado creado (auto-asignado si el rol estaba vacío)",
      );
      await onReload();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Error al crear dispositivo simulado");
    } finally {
      setCreating(null);
    }
  }

  const columns: DataTableColumn<HardwareEventRecord>[] = [
    { key: "when", header: "Fecha", render: (r) => new Date(r.createdAt).toLocaleString("es-AR") },
    { key: "type", header: "Evento", render: (r) => <code className="text-xs">{r.eventType}</code> },
    { key: "device", header: "Dispositivo", render: (r) => devices.find((d) => d.id === r.deviceId)?.name ?? r.deviceId ?? "—" },
  ];

  return (
    <div className="space-y-4">
      <Panel>
        <p className="mb-3 text-sm">Sin hardware físico conectado hoy: toda la plataforma corre sobre drivers simulados.
          Creá un dispositivo de demo por tipo para probar el flujo completo.</p>
        <InvoicingDisclaimer variant="hip" className="mb-3">
          {INVOICING_PATHS.hardwareSimNote}
        </InvoicingDisclaimer>
        <div className="flex flex-wrap gap-2">
          {HARDWARE_DEVICE_TYPES.map((t) => (
            <button
              key={t}
              type="button"
              disabled={creating === t}
              onClick={() => quickCreate(t)}
              className="rounded-lg border border-border px-3 py-1.5 text-xs hover:bg-secondary/40 disabled:opacity-50"
            >
              + {HARDWARE_DEVICE_TYPE_LABELS[t]}
            </button>
          ))}
        </div>
      </Panel>

      <div>
        <p className="mb-2 text-sm font-medium">Últimos eventos (log — base para automation futura)</p>
        {events.length === 0
          ? <CrudEmptyState title="Sin eventos" description="Los eventos aparecen al crear dispositivos o ejecutar acciones." />
          : <DataTable columns={columns} rows={events} getRowKey={(r) => r.id} />}
      </div>
    </div>
  );
}

// ─── Asignaciones (settings por rol operativo) ───────────────────────────────

function BindingsTab({ devices, tenantId }: { devices: HardwareDeviceRecord[]; tenantId: string }) {
  const [bindings, setBindings] = useState<HardwareBindings>({});
  const [roles, setRoles] = useState<Array<{ role: HardwareBindingRole; label: string; deviceTypes: string[] }>>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await apiGetJson<{
        bindings: HardwareBindings;
        roles: Array<{ role: HardwareBindingRole; label: string; deviceTypes: string[] }>;
      }>("/api/hardware/bindings", tenantId);
      setBindings(r.bindings ?? {});
      setRoles(r.roles ?? []);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Error al cargar asignaciones");
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  useEffect(() => { void load(); }, [load]);

  async function save() {
    setSaving(true);
    try {
      const r = await apiSendJson<{ bindings: HardwareBindings }>(
        "/api/hardware/bindings",
        "PUT",
        { bindings },
        tenantId,
      );
      setBindings(r.bindings ?? {});
      toast.success("Asignaciones guardadas. Sin dispositivo = operación manual.");
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Error al guardar");
    } finally {
      setSaving(false);
    }
  }

  if (loading) return <Loading variant="list" rows={4} />;

  return (
    <div className="space-y-4">
      <Panel>
        <p className="text-sm text-muted-foreground">
          Asigná un dispositivo concreto a cada operación del negocio. Si dejás un rol vacío,
          o el dispositivo está deshabilitado / fuera de plan, el flujo sigue <strong>manual</strong>
          (teclado, input, QZ Tray, confirmación a mano).
        </p>
      </Panel>
      <div className="space-y-3">
        {roles.map((r) => {
          const candidates = devices.filter(
            (d) => r.deviceTypes.includes(d.deviceType) && d.enabled,
          );
          return (
            <label key={r.role} className="flex flex-col gap-1 rounded-lg border border-border p-3 sm:flex-row sm:items-center sm:justify-between">
              <span className="text-sm font-medium">{r.label}</span>
              <select
                className="mt-1 w-full rounded-md border border-border bg-background px-2 py-1.5 text-sm sm:mt-0 sm:max-w-xs"
                value={bindings[r.role] ?? ""}
                onChange={(e) => {
                  const v = e.target.value;
                  setBindings((prev) => {
                    const next = { ...prev };
                    if (!v) delete next[r.role];
                    else next[r.role] = v;
                    return next;
                  });
                }}
              >
                <option value="">— Manual (sin hardware) —</option>
                {candidates.map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.name} ({HARDWARE_DEVICE_TYPE_LABELS[d.deviceType]})
                  </option>
                ))}
              </select>
            </label>
          );
        })}
      </div>
      <div className="flex justify-end">
        <button
          type="button"
          disabled={saving}
          onClick={() => void save()}
          className="rounded-lg bg-primary px-4 py-2 text-sm text-primary-foreground disabled:opacity-50"
        >
          {saving ? "Guardando…" : "Guardar asignaciones"}
        </button>
      </div>
    </div>
  );
}
